// Costeo del inventario (costo promedio ponderado móvil): llamadas al API y cálculos de vista previa.
// Los cálculos que valen son los del API; aquí solo se muestran antes de guardar.

import topApi from '../../components/api/ToPApi';

export const CRC = 188;
export const USD = 840;

export type MovementKind =
    'COMPRA' | 'VENTA' | 'DEVOLUCION_CLIENTE' | 'DEVOLUCION_PROVEEDOR' | 'MERMA' | 'CONSUMO_INTERNO' | 'TRASLADO_SALIDA' |
    'TRASLADO_ENTRADA' | 'CONTEO' | 'AJUSTE_ENTRADA' | 'AJUSTE_SALIDA' | 'AJUSTE_COSTO' | 'ALTA' | 'BAJA' | 'REVERSION';

export const KIND_LABELS: Record<MovementKind, string> = {
    COMPRA: 'Compra',
    VENTA: 'Venta',
    DEVOLUCION_CLIENTE: 'Devolución de cliente',
    DEVOLUCION_PROVEEDOR: 'Devolución a proveedor',
    MERMA: 'Merma',
    CONSUMO_INTERNO: 'Consumo interno',
    TRASLADO_SALIDA: 'Traslado enviado',
    TRASLADO_ENTRADA: 'Traslado recibido',
    CONTEO: 'Conteo físico',
    AJUSTE_ENTRADA: 'Ajuste de entrada',
    AJUSTE_SALIDA: 'Ajuste de salida',
    AJUSTE_COSTO: 'Ajuste de costo',
    ALTA: 'Alta de producto',
    BAJA: 'Baja de producto',
    REVERSION: 'Reversión',
};

export const kindLabel = (kind?: string | null, fallback?: string | null) =>
    (kind && KIND_LABELS[kind as MovementKind]) || fallback || '-';

// Movimientos que se revierten uno por uno (los demás se corrigen con su documento)
export const REVERSIBLE_KINDS: string[] = ['AJUSTE_ENTRADA', 'AJUSTE_SALIDA', 'MERMA', 'CONSUMO_INTERNO', 'DEVOLUCION_PROVEEDOR', 'CONTEO', 'ALTA'];

// Salidas que se registran con "−"
export const EXIT_KINDS: { value: string; label: string }[] = [
    { value: 'MERMA', label: 'Merma (dañado, vencido, perdido)' },
    { value: 'CONSUMO_INTERNO', label: 'Consumo interno' },
    { value: 'DEVOLUCION_PROVEEDOR', label: 'Devolución a proveedor' },
    { value: 'AJUSTE_SALIDA', label: 'Otro ajuste de salida' },
];

const n = (value: any) => {
    const x = typeof value === 'string' ? parseFloat(value) : Number(value ?? 0);
    return Number.isFinite(x) ? x : 0;
};

export const round2 = (value: number) => Math.round((value + Number.EPSILON * Math.sign(value)) * 100) / 100;
export const round6 = (value: number) => Math.round((value + Number.EPSILON * Math.sign(value)) * 1e6) / 1e6;

export const currencySymbol = (currency?: number | string | null) => (Number(currency) === USD ? '$' : '₡');

export const formatMoney = (value: any, currency?: number | string | null, decimals = 2) =>
    `${currencySymbol(currency)}${n(value).toLocaleString('es-CR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;

/** Costo unitario: hasta 6 decimales, sin ceros de más. */
export const formatCost = (value: any, currency?: number | string | null) =>
    `${currencySymbol(currency)}${n(value).toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 6 })}`;

export const formatQuantity = (value: any) => n(value).toLocaleString('es-CR', { maximumFractionDigits: 3 });

/**
 * "Otros costos" repartido por valor; la diferencia de redondeo va a la línea de mayor valor
 * (misma regla que el API).
 */
export const allocateOtherCosts = (values: number[], otherCosts: number): number[] => {
    const total = round2(n(otherCosts));
    const base = values.reduce((sum, v) => sum + v, 0);
    if (values.length === 0 || total === 0 || !(base > 0)) return values.map(() => 0);
    const shares = values.map((v) => round2((total * v) / base));
    const diff = round2(total - shares.reduce((sum, s) => sum + s, 0));
    if (diff !== 0) {
        const largest = values.reduce((best, v, i) => (v > values[best] ? i : best), 0);
        shares[largest] = round2(shares[largest] + diff);
    }
    return shares;
};

export interface PurchaseLineForm {
    productId: number | '';
    quantity: string;
    unitCost: string;
    tax: string;
}

export interface PurchasePreviewLine {
    value: number;
    otherCosts: number;
    finalUnitCost: number;
}

/** Vista previa de la compra en la moneda de la compra. */
export const previewPurchase = (lines: PurchaseLineForm[], otherCosts: string) => {
    const values = lines.map((l) => round2(n(l.quantity) * n(l.unitCost)));
    const shares = allocateOtherCosts(values, n(otherCosts));
    const preview: PurchasePreviewLine[] = lines.map((l, i) => ({
        value: values[i],
        otherCosts: shares[i],
        finalUnitCost: n(l.quantity) > 0 ? round6((values[i] + shares[i]) / n(l.quantity)) : 0,
    }));
    return { lines: preview, total: round2(values.reduce((s, v) => s + v, 0) + n(otherCosts)) };
};

export const purchaseError = (form: { lines: PurchaseLineForm[]; currency: number; companyCurrency: number; exchangeRate: string; otherCosts: string }): string | null => {
    if (form.lines.length === 0) return 'Agregue al menos un producto a la compra.';
    if (form.lines.some((l) => !l.productId)) return 'Elija el producto de cada línea.';
    if (form.lines.some((l) => !(n(l.quantity) > 0))) return 'La cantidad de cada línea debe ser mayor que cero.';
    if (form.lines.some((l) => l.unitCost === '' || n(l.unitCost) < 0)) return 'Indique el costo unitario de cada línea.';
    if (n(form.otherCosts) < 0) return '"Otros costos" no puede ser negativo.';
    if (n(form.otherCosts) > 0 && !form.lines.some((l) => n(l.quantity) * n(l.unitCost) > 0)) return 'Para repartir "Otros costos" las líneas deben tener valor.';
    if (form.currency !== form.companyCurrency && !(n(form.exchangeRate) > 0)) return 'Indique el tipo de cambio de la compra.';
    return null;
};

// ---------------------------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------------------------

export interface CostingConfiguration {
    method: string;
    allowNegative: boolean;
    closingDate: string | null;
}

const errorOf = (e: any) => e?.response?.data?.error || 'Ocurrió un error de comunicación, intente de nuevo.';

const call = async <T>(request: () => Promise<{ data: T }>): Promise<{ data?: T; error?: string }> => {
    try {
        const resp = await request();
        return { data: resp.data };
    } catch (e) {
        return { error: errorOf(e) };
    }
};

export const costingApi = {
    configuration: () => call(() => topApi.get<{ configuration: CostingConfiguration; currency: number; isPortalAdmin: boolean }>('/stock/costing/configuration')),
    saveConfiguration: (configuration: { allowNegative: boolean; closingDate: string | null }) =>
        call(() => topApi.put<{ configuration: CostingConfiguration }>('/stock/costing/configuration', { configuration })),
    products: () => call(() => topApi.get<{ stocks: any[] }>('/stock/exportStocks')),
    suppliers: () => call(() => topApi.get<{ suppliers: { supplierCode: number; name: string }[] }>('/supplier/getSuppliers')),
    purchases: (page: number, pageSize = 15) => call(() => topApi.get<any>('/stock/purchases', { params: { page, pageSize } })),
    purchase: (id: number) => call(() => topApi.get<{ lines: any[] }>(`/stock/purchases/${id}`)),
    createPurchase: (purchase: any) => call(() => topApi.post<{ purchaseId: number }>('/stock/purchases', { purchase })),
    cancelPurchase: (id: number, reason: string) => call(() => topApi.post<any>(`/stock/purchases/${id}/cancel`, { reason })),
    transferDestinations: () => call(() => topApi.get<{ destinations: { id: number; name: string }[] }>('/stock/transfers/destinations')),
    transfers: (page: number, pageSize = 15) => call(() => topApi.get<any>('/stock/transfers', { params: { page, pageSize } })),
    createTransfer: (transfer: any) => call(() => topApi.post<{ transferId: number }>('/stock/transfers', { transfer })),
    countSheet: () => call(() => topApi.get<{ products: any[] }>('/stock/counts/sheet')),
    applyCount: (count: { note?: string; lines: { productId: number; counted: number }[] }) =>
        call(() => topApi.post<{ countId: number; counted: number; differences: number }>('/stock/counts', { count })),
    kardex: (id: number, params: { page: number; pageSize?: number; startDate?: string; endDate?: string }) =>
        call(() => topApi.get<any>(`/stock/${id}/kardex`, { params })),
    reverse: (movementId: number, reason: string) => call(() => topApi.post<any>(`/stock/movements/${movementId}/reverse`, { reason })),
    adjustCost: (id: number, cost: number, reason: string) => call(() => topApi.put<any>(`/stock/${id}/cost`, { cost, reason })),
};
